import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { FiX } from 'react-icons/fi';
import { stockOutAPI, productsAPI } from '../../services/api';

const StockOutForm = ({ record, onClose, onSuccess }) => {
  const [products, setProducts] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const isInvoiceLinked = record && record.invoice_id;
  const [formData, setFormData] = useState({
    product_id: '',
    quantity: '',
    location: 'Office',
    unit_price: '',
    customer_name: '',
    customer_phone: '',
    invoice_type: '',
    printing_branding: 'No',
    issue_date: new Date().toISOString().split('T')[0],
    purpose: 'sale',
    notes: '',
    paid_amount: '',
    payment_mode: '',
    payment_status: 'unpaid',
    delivery_status: 'pending'
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchProducts();
  }, []);

  useEffect(() => {
    if (record) {
      setFormData({
        product_id: record.product_id || '',
        quantity: record.quantity || '',
        location: record.location || 'Office',
        unit_price: record.price || '',
        customer_name: record.customer_name || '',
        customer_phone: record.customer_phone || '',
        invoice_type: record.invoice_type || '',
        printing_branding: record.printing_branding || 'No',
        issue_date: record.issue_date ? record.issue_date.split('T')[0] : new Date().toISOString().split('T')[0],
        purpose: record.purpose || 'sale',
        notes: record.notes || '',
        paid_amount: record.paid_amount || '',
        payment_mode: record.payment_mode || '',
        payment_status: record.payment_status || 'unpaid',
        delivery_status: record.delivery_status || 'pending'
      });

      // Set selected product if editing
      if (record.product_id) {
        const product = products.find(p => p.id === record.product_id);
        setSelectedProduct(product);
      }
    }
  }, [record, products]);

  const fetchProducts = async () => {
    try {
      const response = await productsAPI.getAll({ limit: 1000, status: 'active' });
      setProducts(response.data.data);
      console.log("check prodcut: ", response.data)
    } catch (error) {
      console.error('Error fetching products:', error);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });

    if (name === 'product_id') {
      const product = products.find(p => p.id === parseInt(value));
      setSelectedProduct(product);
    }
  };

  const getAvailableStock = () => {
    if (!selectedProduct) return 0;

    if (formData.location === 'Office') {
      return selectedProduct.stock_office || 0;
    } else if (formData.location === 'Godown') {
      return selectedProduct.stock_godown || 0;
    } else if (formData.location === 'Factory') {
      return selectedProduct.stock_factory || 0;
    }
    return 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation - skip stock checks for invoice-linked records
    if (!isInvoiceLinked) {
      if (!formData.product_id) {
        toast.error('Please select a product');
        return;
      }
      if (!formData.quantity || parseInt(formData.quantity) < 1) {
        toast.error('Please enter a valid quantity');
        return;
      }
      if (!formData.unit_price || parseFloat(formData.unit_price) < 0) {
        toast.error('Please enter a valid unit price');
        return;
      }

      const availableStock = getAvailableStock();
      if (parseInt(formData.quantity) > availableStock) {
        toast.error(`Insufficient stock at ${formData.location}. Available: ${availableStock}`);
        return;
      }
    }

    try {
      setLoading(true);
      if (record) {
        await stockOutAPI.update(record.id, formData);
        toast.success('Stock-out record updated successfully');
      } else {
        await stockOutAPI.create(formData);
        toast.success('Stock removed successfully');
      }
      onSuccess();
    } catch (error) {
      console.error('Error saving stock-out record:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div  className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div  className="bg-white rounded-lg shadow-xl w-full max-w-2xl mx-4">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <h2 className="text-xl font-bold text-gray-800">
            {record ? 'Edit Stock Out' : 'Add Stock Out'}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <FiX size={24} />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 " style={{height:"70vh",overflowY:"auto"}}>
          <div className="space-y-4">
            {isInvoiceLinked && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                <p className="text-sm text-yellow-800">
                  This record is linked to an invoice. Product, quantity, location, and price cannot be changed here. Update the invoice to modify those fields.
                </p>
              </div>
            )}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Product */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Product <span className="text-red-500">*</span>
                </label>
                <select
                  name="product_id"
                  value={formData.product_id}
                  onChange={handleChange}
                  className={`w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${isInvoiceLinked ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                  required
                  disabled={isInvoiceLinked}
                >
                  <option value="">Select Product</option>
                  {products.map((product) => (
                    <option key={product.id} value={product.id}>
                      {product.name} ({product.item_code})
                    </option>
                  ))}
                </select>
              </div>

              {/* Location */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Location <span className="text-red-500">*</span>
                </label>
                <select
                  name="location"
                  value={formData.location}
                  onChange={handleChange}
                  className={`w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${isInvoiceLinked ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                  required
                  disabled={isInvoiceLinked}
                >
                  <option value="Office">Office</option>
                  <option value="Godown">Godown</option>
                  <option value="Factory">Factory</option>
                </select>
              </div>

              {/* Available Stock Display */}
              {selectedProduct && (
                <div className="md:col-span-2">
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                    <p className="text-sm text-blue-800">
                      <span className="font-medium">Available Stock at {formData.location}:</span>{' '}
                      {getAvailableStock()} {selectedProduct.unit}
                    </p>
                  </div>
                </div>
              )}

              {/* Quantity */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Quantity <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  name="quantity"
                  value={formData.quantity}
                  onChange={handleChange}
                  min="1"
                  max={isInvoiceLinked ? undefined : getAvailableStock()}
                  className={`w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${isInvoiceLinked ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                  placeholder="Enter quantity"
                  required
                  disabled={isInvoiceLinked}
                />
              </div>

              {/* Unit Price */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Unit Price <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  name="unit_price"
                  value={formData.unit_price}
                  onChange={handleChange}
                  min="0"
                  step="0.01"
                  className={`w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${isInvoiceLinked ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                  placeholder="Enter unit price"
                  required
                  disabled={isInvoiceLinked}
                />
              </div>

              {/* Issue Date */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Issue Date
                </label>
                <input
                  type="date"
                  name="issue_date"
                  value={formData.issue_date}
                  onChange={handleChange}
                  className={`w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${isInvoiceLinked ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                  disabled={isInvoiceLinked}
                />
              </div>

              {/* Customer Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Customer Name
                </label>
                <input
                  type="text"
                  name="customer_name"
                  value={formData.customer_name}
                  onChange={handleChange}
                  className={`w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${isInvoiceLinked ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                  placeholder="Enter customer name"
                  disabled={isInvoiceLinked}
                />
              </div>

              {/* Customer Phone - NEW FIELD */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Customer Phone
                </label>
                <input
                  type="tel"
                  name="customer_phone"
                  value={formData.customer_phone}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter phone number"
                />
              </div>

              {/* Invoice Type */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Invoice Type
                </label>
                <select
                  name="invoice_type"
                  value={formData.invoice_type}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select Type</option>
                  <option value="tax_invoice">Tax Invoice</option>
                  {/* <option value="proforma">Proforma</option>
                  <option value="delivery_challan">Delivery Challan</option> */}
                  <option value="bill_book">Bill Book</option>
                </select>
              </div>

              {/* Printing/Branding */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Printing/Branding
                </label>
                <select
                  name="printing_branding"
                  value={formData.printing_branding}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="No">No</option>
                  <option value="Yes">Yes</option>
                </select>
              </div>

              {/* Purpose */}
              {/* <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Purpose
                </label>
                <select
                  name="purpose"
                  value={formData.purpose}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="sale">Sale</option>
                  <option value="damage">Damage</option>
                  <option value="return">Return</option>
                  <option value="transfer">Transfer</option>
                  <option value="other">Other</option>
                </select>
              </div> */}

              {/* Paid Amount */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Paid Amount
                </label>
                <input
                  type="number"
                  name="paid_amount"
                  value={formData.paid_amount}
                  onChange={handleChange}
                  min="0"
                  step="0.01"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter paid amount"
                />
              </div>

              {/* Payment Mode */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Payment Mode
                </label>
                <select
                  name="payment_mode"
                  value={formData.payment_mode}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select Mode</option>
                  <option value="cash">Cash</option>
                  <option value="online">Online Transfer</option>
                  <option value="upi">UPI</option>
                  <option value="cheque">Cheque</option>
                  <option value="card">Card</option>
                </select>
              </div>

              {/* Payment Status */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Payment Status
                </label>
                <select
                  name="payment_status"
                  value={formData.payment_status}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="unpaid">Unpaid</option>
                  <option value="partial">Partial</option>
                  <option value="paid">Paid</option>
                </select>
              </div>

              {/* Delivery Status */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Delivery Status
                </label>
                <select
                  name="delivery_status"
                  value={formData.delivery_status}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="pending">Pending</option>
                  <option value="delivered">Delivered</option>
                  <option value="returnable_basis">Returnable Basis</option>
                </select>
              </div>

              {/* Due Amount (Read-only, calculated) */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Due Amount
                </label>
                <input
                  type="text"
                  value={`₹${(((parseFloat(formData.quantity) || 0) * (parseFloat(formData.unit_price) || 0)) - (parseFloat(formData.paid_amount) || 0)).toFixed(2)}`}
                  readOnly
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-900 font-medium"
                />
              </div>
            </div>

            {/* Notes */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Notes
              </label>
              <textarea
                name="notes"
                value={formData.notes}
                onChange={handleChange}
                rows="3"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Enter any additional notes"
              />
            </div>

            {/* Stock Summary - Shows when product is selected */}
            {selectedProduct && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <h3 className="text-sm font-semibold text-red-800 mb-2">Stock Summary After Removal</h3>
                {(() => {
                  const qty = parseInt(formData.quantity) || 0;
                  const currentStock = formData.location === 'Office' ? (selectedProduct.stock_office || 0) :
                                       formData.location === 'Godown' ? (selectedProduct.stock_godown || 0) :
                                       (selectedProduct.stock_factory || 0);
                  const newStock = Math.max(0, currentStock - qty);
                  const isInsufficient = qty > currentStock;
                  return (
                    <div className="grid grid-cols-3 gap-4 text-sm">
                      <div>
                        <span className="text-gray-600">Office:</span>
                        <span className={`ml-1 font-medium ${formData.location === 'Office' ? (isInsufficient ? 'text-red-600' : 'text-orange-600') : 'text-gray-800'}`}>
                          {formData.location === 'Office' ? `${selectedProduct.stock_office || 0} → ${newStock}` : (selectedProduct.stock_office || 0)}
                        </span>
                      </div>
                      <div>
                        <span className="text-gray-600">Godown:</span>
                        <span className={`ml-1 font-medium ${formData.location === 'Godown' ? (isInsufficient ? 'text-red-600' : 'text-orange-600') : 'text-gray-800'}`}>
                          {formData.location === 'Godown' ? `${selectedProduct.stock_godown || 0} → ${newStock}` : (selectedProduct.stock_godown || 0)}
                        </span>
                      </div>
                      <div>
                        <span className="text-gray-600">Factory:</span>
                        <span className={`ml-1 font-medium ${formData.location === 'Factory' ? (isInsufficient ? 'text-red-600' : 'text-orange-600') : 'text-gray-800'}`}>
                          {formData.location === 'Factory' ? `${selectedProduct.stock_factory || 0} → ${newStock}` : (selectedProduct.stock_factory || 0)}
                        </span>
                      </div>
                      <div className="col-span-3 pt-2 border-t border-red-200">
                        <span className="text-gray-600">Total Stock:</span>
                        <span className={`ml-1 font-semibold ${isInsufficient ? 'text-red-600' : 'text-red-800'}`}>
                          {selectedProduct.total_stock || 0} → {Math.max(0, (selectedProduct.total_stock || 0) - qty)}
                        </span>
                        {isInsufficient && (
                          <span className="ml-2 text-red-600 text-xs">(Insufficient stock!)</span>
                        )}
                      </div>
                    </div>
                  );
                })()}
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="flex gap-3 mt-6">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
              disabled={loading}
            >
              {loading ? 'Saving...' : record ? 'Update' : 'Remove Stock'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default StockOutForm;